import {useContext, useEffect} from 'react';

import {FormikErrors} from 'formik';
import {useTranslation} from 'react-i18next';

import {Grid, Typography} from '@mui/material';

import {SimpleFormikForm} from '../../../../../../components/SimpleFormikForm/SimpleFormikForm';
import {FieldType} from '../../../../../../components/SimpleFormikForm/types';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {Post} from '../../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../../lib/api/POST/PostRoutes';
import {CompanyAdminsRequestBodies} from '../../../../../../lib/api/RequestBodies/features/company/admins/CompanyAdminsRequestBodies';
import {FORBIDDEN_DOMAINS} from '../../../../../../utils/forbiddenDomains';
import {validateEmail} from '../../../../../../utils/validators';

interface Props {
  companyId: string;
  onCreate: () => void;
}
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
}

const Header = ({label}: {label: string}) => (
  <Typography variant='h4' marginBottom={'6px'} marginTop='15px'>
    {label}
  </Typography>
);

export const AddAdminModal = ({companyId, onCreate}: Props) => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);

  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
  };

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    if (values.firstName === '') {
      errors.firstName = t('registration.signUp.formLabels.errorMessages.firstName') || 'First name is required';
    }
    if (values.lastName === '') {
      errors.lastName = t('registration.signUp.formLabels.errorMessages.lastName') || 'Last name is required';
    }
    if (!values.email) {
      errors.email = t('registration.signUp.formLabels.errorMessages.email') || 'Email is required';
    } else if (!validateEmail(values.email)) {
      errors.email = t('registration.signUp.formLabels.errorMessages.invalidEmail') || 'Invalid email';
    } else if (FORBIDDEN_DOMAINS.includes(values.email.split('@')[1])) {
      errors.email = t('registration.signUp.formLabels.errorMessages.companyEmail') || 'Invalid email';
    }
    return errors;
  };

  const fields: FieldType<keyof FormValues>[] = [
    {
      componentType: 'text-field',
      fieldHeader: <Header label={t('companyAdmins.firstName') || 'First Name'} />,
      fieldName: 'firstName',
      type: 'text',
      testId: 'first-name-add-admin-field',
    },
    {
      componentType: 'text-field',
      fieldName: 'lastName',
      fieldHeader: <Header label={t('companyAdmins.lastName') || 'Last Name'} />,
      type: 'text',
      testId: 'last-name-add-admin-field',
    },
    {
      componentType: 'text-field',
      fieldName: 'email',
      fieldHeader: <Header label={t('companyAdmins.email') || 'Email'} />,
      type: 'email',
      testId: 'email-add-admin-field',
    },
  ];

  const {makeRequest, error} = useApiWrite<Post.CreateCompanyAdmin, CompanyAdminsRequestBodies['createCompanyAdmin']>({
    method: 'post',
    path: PostRoutes[Post.CreateCompanyAdmin](companyId),
    onSuccess: onCreate,
  });

  const onSubmit = (values: FormValues) => {
    makeRequest({
      companyAdmin: values,
    });
  };

  useEffect(() => {
    if (error) {
      dispatchMessage(t('shared.formSubmit.creationError'), 'error');
    }
  }, [error, dispatchMessage, t]);

  return (
    <Grid width='100%'>
      <Typography variant='h3' gutterBottom>
        {t('companyAdmins.addAdmin')}
      </Typography>
      <SimpleFormikForm
        initialValues={initialValues}
        buttonText={t('shared.formButtons.save')}
        fields={fields}
        validate={validate}
        buttonPosition='flex-end'
        buttonFullWidth={false}
        onSubmit={onSubmit}
      />
    </Grid>
  );
};
